import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatCurrency, formatInteger } from '@/lib/format'
import fileIcon from '@/assets/contracts/metric-file.svg'
import contractIcon from '@/assets/contracts/metric-contract.svg'
import refreshIcon from '@/assets/contracts/metric-refresh.svg'
import alertIcon from '@/assets/contracts/metric-alert.svg'
import type { ContractSummary } from '@/types'

type MetricTone = 'active' | 'pending' | 'renew' | 'expired'
export type ContractMetricKey = MetricTone
export type ContractMetricActions = Partial<
  Record<ContractMetricKey, () => void>
>

const TONE: Record<
  MetricTone,
  {
    card: string
    iconSrc: string
    iconClass: string
    width: number
    height: number
    valueClass: string
  }
> = {
  active: {
    card: 'border-[#a7f3d0] bg-[#ecfdf5]',
    iconSrc: fileIcon,
    iconClass: 'h-[14.93px] w-[12.27px]',
    width: 12.27,
    height: 14.93,
    valueClass: 'text-[#171a20]',
  },
  pending: {
    card: 'border-[#fde68a] bg-[#fbf3d9]',
    iconSrc: contractIcon,
    iconClass: 'size-3.5',
    width: 14,
    height: 14,
    valueClass: 'text-[#171a20]',
  },
  renew: {
    card: 'border-[#bfdbfe] bg-[#ebf1fe]',
    iconSrc: refreshIcon,
    iconClass: 'h-[13.6px] w-[16.27px]',
    width: 16.27,
    height: 13.6,
    valueClass: 'text-[#171a20]',
  },
  expired: {
    card: 'border-[#fca5a5] bg-[#fde9e8]',
    iconSrc: alertIcon,
    iconClass: 'size-[14.93px]',
    width: 14.93,
    height: 14.93,
    valueClass: 'text-[#d63b33]',
  },
}

const cardShadow =
  'shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
const iconShadow = 'shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
const summaryCardInteractionClass =
  'cursor-pointer transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
const summaryCardActiveColor = '#ff5a1f'

function MetricCard({
  tone,
  label,
  value,
  subtitle,
  loading,
  onClick,
  active,
}: {
  tone: MetricTone
  label: string
  value: string
  subtitle: string
  loading?: boolean
  onClick?: () => void
  active?: boolean
}) {
  const theme = TONE[tone]
  const content = (
    <div className='flex items-center gap-4'>
      <div
        className={cn(
          'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-2',
          iconShadow
        )}
      >
        <span className='flex size-4 items-center justify-center overflow-hidden'>
          <img
            src={theme.iconSrc}
            alt=''
            className={cn('block max-w-none', theme.iconClass)}
            width={theme.width}
            height={theme.height}
          />
        </span>
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-2'>
        <p className='font-heading text-xs font-medium leading-[1.2] text-[#5a5a5a]'>
          {label}
        </p>
        <div className='flex flex-col gap-1'>
          {loading ? (
            <Skeleton className='h-7.25 w-16' />
          ) : (
            <p
              className={cn(
                'font-heading h-7.25 truncate text-[24px] font-semibold leading-[1.2] tracking-[-0.48px]',
                theme.valueClass
              )}
            >
              {value}
            </p>
          )}
          {loading ? (
            <Skeleton className='h-4 w-28' />
          ) : (
            <p className='truncate text-xs font-medium leading-4 text-[#5a5a5a]'>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  )
  const className = cn(
    'flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border p-4 text-left',
    cardShadow,
    theme.card,
    onClick && summaryCardInteractionClass
  )
  const style = active ? { borderColor: summaryCardActiveColor } : undefined

  if (onClick) {
    return (
      <button
        type='button'
        aria-pressed={active}
        className={className}
        style={style}
        onClick={onClick}
      >
        {content}
      </button>
    )
  }

  return (
    <div className={className} style={style}>
      {content}
    </div>
  )
}

function displayCount(value: number | null | undefined): string {
  if (value == null) return '—'
  return formatInteger(value)
}

function displayYearlyAmount(value: number | null | undefined): string {
  return formatCurrency(value ?? 0, 'EUR', 2)
}

// Contracts list metrics (Figma 3396:160432): Active / Awaiting signature /
// Renew within 30 days / Expired.
export function ContractMetricsSection({
  summary,
  loading = false,
  className,
  actions,
  activeKey,
}: {
  summary?: Partial<ContractSummary> | null
  loading?: boolean
  className?: string
  actions?: ContractMetricActions
  activeKey?: ContractMetricKey
}) {
  const { t } = useTranslation('contracts')
  const pendingThisMonthCount = summary?.pending_signature_this_month ?? 0

  return (
    <section
      className={cn(
        'flex shrink-0 flex-col gap-4 sm:flex-row sm:items-stretch',
        className
      )}
      aria-label={t('list.kpi.ariaLabel')}
    >
      <MetricCard
        tone='active'
        label={t('list.kpi.active')}
        value={displayCount(summary?.active)}
        subtitle={t('list.kpi.activeAmount', {
          amount: displayYearlyAmount(summary?.active_amount),
        })}
        onClick={actions?.active}
        active={activeKey === 'active'}
        loading={loading}
      />
      <MetricCard
        tone='pending'
        label={t('list.kpi.pendingSignature')}
        value={displayCount(summary?.pending_signature)}
        subtitle={
          pendingThisMonthCount === 0
            ? t('list.kpi.pendingEmpty')
            : t('list.kpi.pendingCount', { count: pendingThisMonthCount })
        }
        onClick={actions?.pending}
        active={activeKey === 'pending'}
        loading={loading}
      />
      <MetricCard
        tone='renew'
        label={t('list.kpi.toRenew')}
        value={displayCount(summary?.to_renew)}
        subtitle={t('list.kpi.renewAmount', {
          amount: displayYearlyAmount(summary?.to_renew_amount),
        })}
        onClick={actions?.renew}
        active={activeKey === 'renew'}
        loading={loading}
      />
      <MetricCard
        tone='expired'
        label={t('list.kpi.expired')}
        value={displayCount(summary?.expired)}
        subtitle={t('list.kpi.expiredHint')}
        onClick={actions?.expired}
        active={activeKey === 'expired'}
        loading={loading}
      />
    </section>
  )
}

export default ContractMetricsSection
